import React, { FC, memo, useCallback, useState, useMemo } from 'react';
import { WarehouseForBooking, getWorkingScheduleString } from 'entities/Warehouse';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ReactComponent as CallIcon } from 'shared/assets/icons/newIcons/CallIcon.svg';
import { ReactComponent as SendEmailIcon } from 'shared/assets/icons/newIcons/SendEmailIcon.svg';
import { ReactComponent as TimeIcon } from 'shared/assets/icons/newIcons/TimeIcon.svg';
import { ReactComponent as ShareIcon } from 'shared/assets/icons/newIcons/ShareIcon.svg';
import { Button } from 'shared/ui/Button';
import { ModernSizeGroupsTags } from 'entities/SizeCode';
import { getBoxDiscountedPriceAmount, getBoxPriceAmountWithVat, useGetMinBoxPriceAmount, Box } from 'entities/Box';
import { useGetCurrencySymbol, getGlobalSettings } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getLoggedUserData } from 'entities/User';
import { showNotification } from 'app/providers/NotificationsProvider';
import { BoxSelectionModal } from 'features/ModernSizeCodeSelect/ui/BoxSelectionModal';
import { RentOptionDocument } from 'entities/RentOption';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface WarehouseSizesCardProps {
  warehouse: WarehouseForBooking;
  rentOptions: RentOptionDocument[];
  selectedBoxId: string | undefined;
  onSelectBox: (box: Box, sizeCodeId: string) => void;
  onBookBox: (box: Box, sizeCodeId: string) => void;
}

export const WarehouseSizesCard: FC<WarehouseSizesCardProps> = memo((props) => {
  const { warehouse, rentOptions, selectedBoxId, onSelectBox, onBookBox } = props;
  const { warehouseId, phone, email, workingSchedule, sizeCodes, availableBoxesAmount, warehouseDiscounts, minPrice } = warehouse;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const currencySymbol = useGetCurrencySymbol();
  const globalSettings = useAppSelector(getGlobalSettings);
  const loggedUser = useAppSelector(getLoggedUserData);
  const { t } = useAppTranslation(['booking', 'common']);

  const workingScheduleString = getWorkingScheduleString(workingSchedule, t);

  const minBoxPrice = useGetMinBoxPriceAmount(warehouse);
  const discountedPrice = getBoxDiscountedPriceAmount({ warehouseDiscounts, boxPrice: minPrice });
  const discountedBoxPriceWithVat = getBoxPriceAmountWithVat(discountedPrice, warehouse, loggedUser?.accountType);
  const discountedBoxPrice = globalSettings?.showPriceWithVat ? discountedBoxPriceWithVat : discountedPrice;

  // Find cheapest available size code (by minBoxPrice)
  const cheapestAvailableSizeCode = useMemo(() => {
    const availableSizeCodes = sizeCodes?.filter((sizeCode) => sizeCode.availableBoxesCount > 0) || [];
    if (!availableSizeCodes.length) return null;

    return availableSizeCodes.reduce((cheapest, current) => {
      if (!cheapest.minBoxPrice) return current;
      if (!current.minBoxPrice) return cheapest;
      return current.minBoxPrice < cheapest.minBoxPrice ? current : cheapest;
    });
  }, [sizeCodes]);

  const handleShow = useCallback((): void => {
    const target = document.getElementById('warehouse-features-section');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSelectBox = useCallback(
    (box: Box) => {
      if (cheapestAvailableSizeCode) {
        onSelectBox(box, cheapestAvailableSizeCode.sizeCodeId);
      }
    },
    [cheapestAvailableSizeCode, onSelectBox],
  );

  const handleBookBox = useCallback(
    (box: Box) => {
      if (cheapestAvailableSizeCode) {
        onBookBox(box, cheapestAvailableSizeCode.sizeCodeId);
        handleCloseModal();
      }
    },
    [cheapestAvailableSizeCode, onBookBox, handleCloseModal],
  );

  const handleShare = useCallback((): void => {
    const baseUrl = window.location.origin + window.location.pathname;
    const shareUrl = `${baseUrl}?warehouseId=${warehouseId}`;

    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          showNotification('info', t('Success', { ns: 'common' }), t('Link is copied to clipboard', { ns: 'common' }));
        })
        .catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = shareUrl;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          showNotification('info', t('Success', { ns: 'common' }), t('Link is copied to clipboard', { ns: 'common' }));
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showNotification('info', t('Success', { ns: 'common' }), t('Link is copied to clipboard', { ns: 'common' }));
    }
  }, [warehouseId, t]);

  return (
    <>
      <div className="bg-white rounded-lg p-5 w-[350px] flex flex-col">
        <div className="flex justify-between items-start mb-5">
          <div className="flex-1">
            <div className="space-y-2">
              <div className="flex items-center text-grey text-sm">
                <CallIcon className="w-5 h-5 mr-2 text-grey" />
                <span>{phone}</span>
              </div>

              <div className="flex items-center text-grey text-sm">
                <SendEmailIcon className="w-5 h-5 mr-2 text-grey" />
                <span>{email}</span>
              </div>

              <div className="flex items-center text-grey text-sm">
                <TimeIcon className="w-5 h-5 mr-2 text-grey" />
                <span>{workingScheduleString}</span>
              </div>
            </div>
          </div>

          <Button
            theme="outlined"
            size="medium"
            className="border-black shrink-0 ml-4 w-[40px] h-[40px] p-0 flex items-center justify-center"
            icon={<ShareIcon className="w-[20px] h-[20px] text-primary" />}
            onClick={handleShare}
          />
        </div>

        <div className="mb-3">
          <ModernSizeGroupsTags sizeCodes={sizeCodes} />
        </div>

        <div className="text-grey text-md mb-2">{t('{{availableBoxesAmount}} boxes free', { availableBoxesAmount })}</div>

        <div className="mt-auto">
          <div className="flex flex-col space-y-3">
            {Boolean(minBoxPrice) && (
              <div className="text-lg ml-auto font-semibold text-primary font-tektur">
                {t('From {{discountedPrice}} {{currencySymbol}}/month', {
                  currencySymbol,
                  discountedPrice: formatPrice(discountedBoxPrice || minBoxPrice),
                })}
              </div>
            )}

            <Button onClick={handleShow} isDisabled={!cheapestAvailableSizeCode} className="w-full" theme="primary">
              {t('Show')}
            </Button>
          </div>
        </div>
      </div>

      {cheapestAvailableSizeCode && (
        <BoxSelectionModal
          isOpen={isModalOpen}
          sizeCode={cheapestAvailableSizeCode}
          rentOptions={rentOptions}
          selectedBoxId={selectedBoxId}
          onClose={handleCloseModal}
          onSelectBox={handleSelectBox}
          onBookBox={handleBookBox}
        />
      )}
    </>
  );
});
